/* =========================================================
   LFDL Custom Predictive Search
   - Tolérance fautes (fallback tokens / requêtes alternatives)
   - Jamais "0 résultat" sec -> fallback produits (products.json)
   - Catégories : Produits / Collections / Articles / Pages
   ========================================================= */

(function () {
  // Par défaut on cible tes inputs custom.
  // Bonus: on s'accroche aussi aux champs de recherche standards (forms /search)
  // pour que header + page recherche + tout autre usage partagent le même moteur.
  const SELECTOR_INPUT = "[data-lfdl-search-input], form[action='/search'] input[name='q'], form[action*='/search'] input[name='q']";
  const SELECTOR_DROPDOWN = "[data-lfdl-search-dropdown]";

  const MIN_CHARS = 1;
  const LIMIT_PER_TYPE = 8;
  const DEBOUNCE_MS = 170;

  let debounceTimer = null;
  let controller = null;

  const decodeEntities = (str) => {
    if (typeof str !== "string") str = str == null ? "" : String(str);
    try {
      const ta = document.createElement("textarea");
      ta.innerHTML = str;
      return ta.value;
    } catch (e) {
      return str;
    }
  };

  const normalize = (s) =>
    decodeEntities(s || "")
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[’‘]/g, "'")
      .replace(/[’`]/g, "'")
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "");

  // Cache simple pour éviter de re-taper le endpoint suggest à chaque frappe.
  const suggestCache = new Map();

  // "Intelligence" : génère une poignée de requêtes candidates (typos, voyelles, transpositions)
  // puis on interroge Shopify /search/suggest.json en cascade jusqu'à trouver des résultats.
  // Objectif: éviter le "mot par mot" et tenir un misspell type "ancens" -> "encens".
  const VOWELS = ["a", "e", "i", "o", "u", "y"];

  function cleanSpaces(s) {
    return (s || "").toString().trim().replace(/\s+/g, " ");
  }

  function baseKey(s) {
    // Key "fort" pour dédoublonner (sans accents + lowercase).
    return normalize(cleanSpaces(s).replace(/['’]/g, ""));
  }

  function tokenVariants(token, max = 10) {
    const t = baseKey(token);
    const out = [];
    const seen = new Set();

    const add = (x) => {
      const k = baseKey(x);
      if (!k || k === t) return;
      if (k.length < 3) return;
      if (seen.has(k)) return;
      seen.add(k);
      out.push(k);
    };

    // Transposition (typo clavier classique)
    if (t.length >= 4) {
      const i = t.length - 2;
      add(t.slice(0, i) + t[i + 1] + t[i] + t.slice(i + 2));
    }

    // Compress doubles (ex: "incenns" -> "incens")
    add(t.replace(/(.)\1+/g, "$1"));

    // Voyelles: substitutions limitées (on priorise le début du mot)
    for (let i = 0; i < t.length && out.length < max; i++) {
      const ch = t[i];
      if (!VOWELS.includes(ch)) continue;
      for (let j = 0; j < VOWELS.length && out.length < max; j++) {
        const v = VOWELS[j];
        if (v === ch) continue;
        add(t.slice(0, i) + v + t.slice(i + 1));
      }
    }

    // Switch "an" <-> "en" en début (ultra fréquent en FR)
    if (t.startsWith("an")) add("en" + t.slice(2));
    if (t.startsWith("en")) add("an" + t.slice(2));

    // Suppressions ciblées (quand une lettre a sauté)
    if (t.length >= 4) {
      add(t.slice(0, -1));          // enlève dernier char
      add(t.slice(1));              // enlève premier char
      const mid = Math.floor(t.length / 2);
      add(t.slice(0, mid) + t.slice(mid + 1));
    }

    return out.slice(0, max);
  }

  function buildCandidateQueries(qRaw, max = 14) {
    const q0 = cleanSpaces(qRaw);
    const qKey = baseKey(q0);
    const candidates = [];
    const seen = new Set();

    const push = (raw) => {
      const s = cleanSpaces(raw);
      const k = baseKey(s);
      if (!k || k.length < MIN_CHARS) return;
      if (seen.has(k)) return;
      seen.add(k);
      candidates.push(s);
    };

    push(q0);

    // Nettoyages soft
    if (q0.includes("-")) push(q0.replace(/-/g, " "));
    if (q0.includes("'")) push(q0.replace(/['’]/g, ""));

    // Version sans accents / lowercase (peut aider selon le contenu)
    if (qKey && qKey !== q0) push(qKey);

    // Si multi-mots, tente aussi le 1er token
    const tokens = (qKey || "").split(/\s+/).filter(Boolean);
    if (tokens.length > 1) push(tokens[0]);

    // Version raccourcie (typo fin de mot)
    if (qKey && qKey.length > 3) push(qKey.slice(0, -1));

    // Variantes token par token
    for (let i = 0; i < tokens.length && candidates.length < max; i++) {
      const vars = tokenVariants(tokens[i], 10);
      for (let j = 0; j < vars.length && candidates.length < max; j++) {
        const clone = tokens.slice();
        clone[i] = vars[j];
        push(clone.join(" "));
      }
    }

    return candidates.slice(0, max);
  }
  // ============================================================
  // Scoring TITRE (prioritaire) + tolérance fautes légères
  // Objectif: éviter les résultats "hors sujet" et sortir plus tôt des matches utiles
  // ============================================================

  function hasAnyBigram(q, w) {
    if (!q || q.length < 2) return false;
    if (!w) return false;
    for (let i = 0; i < q.length - 1; i++) {
      const bg = q.slice(i, i + 2);
      if (w.indexOf(bg) !== -1) return true;
    }
    return false;
  }

  function levenshteinBounded(a, b, maxDist) {
    if (a === b) return 0;
    const al = a.length;
    const bl = b.length;
    const diff = Math.abs(al - bl);
    if (diff > maxDist) return maxDist + 1;
    if (al === 0) return bl <= maxDist ? bl : maxDist + 1;
    if (bl === 0) return al <= maxDist ? al : maxDist + 1;

    let prev = new Array(bl + 1);
    let curr = new Array(bl + 1);

    for (let j = 0; j <= bl; j++) prev[j] = j;

    for (let i = 1; i <= al; i++) {
      curr[0] = i;
      let rowMin = curr[0];

      const ai = a.charCodeAt(i - 1);
      for (let j = 1; j <= bl; j++) {
        const cost = ai === b.charCodeAt(j - 1) ? 0 : 1;

        const ins = curr[j - 1] + 1;
        const del = prev[j] + 1;
        const sub = prev[j - 1] + cost;

        let v = ins < del ? ins : del;
        v = sub < v ? sub : v;

        curr[j] = v;
        rowMin = v < rowMin ? v : rowMin;
      }

      if (rowMin > maxDist) return maxDist + 1;

      const tmp = prev;
      prev = curr;
      curr = tmp;
    }

    return prev[bl];
  }

  function minDistToSubstrings(q, w, maxDist) {
    const ql = q.length;
    const wl = w.length;
    if (wl === 0) return maxDist + 1;
    if (wl < ql) return levenshteinBounded(q, w, maxDist);
    let best = maxDist + 1;

    for (let i = 0; i <= wl - ql; i++) {
      const seg = w.slice(i, i + ql);
      const d = levenshteinBounded(q, seg, maxDist);
      if (d < best) best = d;
      if (best === 0) return 0;
      if (best === 1 && maxDist === 1) return 1;
    }

    if (maxDist >= 1 && wl >= ql + 1) {
      const l2 = ql + 1;
      for (let i = 0; i <= wl - l2; i++) {
        const seg = w.slice(i, i + l2);
        const d = levenshteinBounded(q, seg, maxDist);
        if (d < best) best = d;
        if (best === 0) return 0;
        if (best === 1 && maxDist === 1) return 1;
      }
    }

    return best;
  }

  function scoreTitleMatch(queryRaw, titleRaw) {
    const qk = baseKey(queryRaw || "");
    const tk = baseKey(titleRaw || "");
    if (!qk || !tk) return 0;

    if (tk === qk) return 1000;
    if (tk.indexOf(qk) === 0) return 950;

    const words = tk.split(" ").filter(Boolean);
    for (let i = 0; i < words.length; i++) {
      if (words[i].indexOf(qk) === 0) return 900;
    }

    if (tk.indexOf(qk) !== -1) return 850;

    if (qk.length < 3) return 0;

    const maxDist = qk.length <= 4 ? 1 : 2;

    let best = maxDist + 1;
    for (let i = 0; i < words.length; i++) {
      const w = words[i];
      if (!hasAnyBigram(qk, w)) continue;
      const d = minDistToSubstrings(qk, w, maxDist);
      if (d < best) best = d;
      if (best === 0) break;
    }

    if (best > maxDist) return 0;

    if (best === 0) return 840;
    if (best === 1) return 780;
    return 720;
  }

  function filterAndRankByTitle(items, query) {
    if (!Array.isArray(items) || !items.length) return [];

    const scored = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const s = scoreTitleMatch(query, it && it.title);
      if (s <= 0) continue;
      scored.push({ it: it, s: s });
    }

    scored.sort((a, b) => b.s - a.s);

    const out = [];
    for (let i = 0; i < scored.length && out.length < LIMIT_PER_TYPE; i++) out.push(scored[i].it);
    return out;
  }


  function mergeUnique(primary, secondary, keyFn) {
    const out = [];
    const seen = new Set();
    const add = (arr) => {
      if (!Array.isArray(arr)) return;
      for (let i = 0; i < arr.length; i++) {
        const it = arr[i];
        if (!it) continue;
        const k = keyFn ? keyFn(it) : (it.url || it.handle || it.title);
        if (!k) continue;
        if (seen.has(k)) continue;
        seen.add(k);
        out.push(it);
      }
    };
    add(primary);
    add(secondary);
    return out;
  }

  // ============================================================
  // Index LOCAL des collections (custom + smart), pour matcher comme ta page "Liste des collections"
  // ============================================================

  const COLLECTIONS_INDEX_KEY = "lfdl_collections_index_v1";
  const COLLECTIONS_INDEX_TTL_MS = 24 * 60 * 60 * 1000;

  let collectionsIndex = null;
  let collectionsIndexPromise = null;

  function readCollectionsIndexFromStorage() {
    try {
      const raw = localStorage.getItem(COLLECTIONS_INDEX_KEY);
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if (!obj || !Array.isArray(obj.items) || typeof obj.ts !== "number") return null;
      if (Date.now() - obj.ts > COLLECTIONS_INDEX_TTL_MS) return null;
      return obj.items;
    } catch (e) {
      return null;
    }
  }

  function writeCollectionsIndexToStorage(items) {
    try {
      localStorage.setItem(COLLECTIONS_INDEX_KEY, JSON.stringify({ ts: Date.now(), items: items }));
    } catch (e) {}
  }

  async function fetchJSONTimeout(url, timeoutMs) {
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const r = await fetch(url, { signal: controller.signal, credentials: "same-origin" });
      clearTimeout(t);
      if (!r.ok) return null;
      return await r.json();
    } catch (e) {
      clearTimeout(t);
      return null;
    }
  }

  function normalizeCollectionItem(x) {
    if (!x) return null;
    const title = decodeEntities(x.title || "");
    const handle = x.handle || "";
    if (!title || !handle) return null;

    const url = "/collections/" + handle;

    let img = "";
    if (x.image && typeof x.image === "object") img = x.image.src || x.image.url || "";
    if (!img && x.image_src) img = x.image_src;

    return { title: title, url: url, featured_image: img ? { url: img } : null };
  }

  async function fetchCollectionsEndpoint(endpoint, listKey) {
    const out = [];
    const limit = 250;
    const maxPages = 20;

    for (let page = 1; page <= maxPages; page++) {
      const url = endpoint + "?limit=" + String(limit) + "&page=" + String(page);
      const json = await fetchJSONTimeout(url, 2400);
      if (!json) break;

      const list = json[listKey];
      if (!Array.isArray(list) || list.length === 0) break;

      for (let i = 0; i < list.length; i++) {
        const it = normalizeCollectionItem(list[i]);
        if (it) out.push(it);
      }

      if (list.length < limit) break;
    }

    return out;
  }


  async function fetchCollectionsJsonAll() {
    try {
      const out = [];
      let page = 1;
      for (; page <= 20; page++) {
        const url = new URL("/collections.json", window.location.origin);
        url.searchParams.set("limit", "250");
        url.searchParams.set("page", String(page));
        const data = await fetchJSONTimeout(url.toString(), 15000);
        const arr = Array.isArray(data && data.collections) ? data.collections : [];
        if (!arr.length) break;
        for (let i = 0; i < arr.length; i++) out.push(normalizeCollectionItem(arr[i]));
        if (arr.length < 250) break;
      }
      return out;
    } catch (e) {
      return [];
    }
  }

  async function buildCollectionsIndex() {
    const cached = readCollectionsIndexFromStorage();
    if (cached && cached.length) return cached;

        let all = await fetchCollectionsJsonAll();

    if (!all || !all.length) {
      const [custom, smart] = await Promise.allSettled([
        fetchCollectionsEndpoint("/custom_collections.json", "custom_collections"),
        fetchCollectionsEndpoint("/smart_collections.json", "smart_collections")
      ]);

      all = [];
      if (custom.status === "fulfilled" && Array.isArray(custom.value)) all.push.apply(all, custom.value);
      if (smart.status === "fulfilled" && Array.isArray(smart.value)) all.push.apply(all, smart.value);
    }

    const byUrl = new Map();
    for (let i = 0; i < all.length; i++) {
      const it = all[i];
      if (!it || !it.url || !it.title) continue;
      if (!byUrl.has(it.url)) byUrl.set(it.url, it);
    }

    const merged = Array.from(byUrl.values());
    if (merged.length) writeCollectionsIndexToStorage(merged);

    return merged;
  }

  function ensureCollectionsIndex() {
    if (Array.isArray(collectionsIndex) && collectionsIndex.length) return Promise.resolve(collectionsIndex);
    if (collectionsIndexPromise) return collectionsIndexPromise;

    collectionsIndexPromise = buildCollectionsIndex()
      .then((items) => {
        collectionsIndex = Array.isArray(items) ? items : [];
        return collectionsIndex;
      })
      .catch(() => {
        collectionsIndex = [];
        return collectionsIndex;
      });

    return collectionsIndexPromise;
  }



  
  /* =========================================================
     Index LOCAL des produits
  ========================================================= */

  const PRODUCTS_INDEX_KEY = "lfdl_ps_products_index_v1";
  const PRODUCTS_INDEX_TS_KEY = "lfdl_ps_products_index_ts_v1";
  const PRODUCTS_INDEX_TTL_MS = 1000 * 60 * 60 * 24 * 7; // 7 jours
  const PRODUCTS_INDEX_VIEW_URL = "/collections/all?view=lfdl-ps-products";
  const PRODUCTS_INDEX_PAGE_CAP = 40; // 40 pages * 250 = 10000 produits max

  let productsIndex = null;
  let productsIndexPromise = null;

  function normalizeProductItem(p) {
    const title = (p && p.title ? String(p.title) : "").trim();
    const url = (p && p.url ? String(p.url) : "").trim();
    const image = p && p.image ? String(p.image) : null;
    const price = p && p.price ? String(p.price) : null;
    const compare_at_price = p && p.compare_at_price ? String(p.compare_at_price) : null;
    return { title, url, image, price, compare_at_price };
  }

  function readProductsIndexFromStorage() {
    try {
      const raw = localStorage.getItem(PRODUCTS_INDEX_KEY);
      const tsRaw = localStorage.getItem(PRODUCTS_INDEX_TS_KEY);
      if (!raw || !tsRaw) return null;
      const ts = parseInt(tsRaw, 10);
      if (!Number.isFinite(ts)) return null;
      if (Date.now() - ts > PRODUCTS_INDEX_TTL_MS) return null;
      const arr = JSON.parse(raw);
      if (!Array.isArray(arr)) return null;
      return arr;
    } catch (e) {
      return null;
    }
  }

  function writeProductsIndexToStorage(items) {
    try {
      localStorage.setItem(PRODUCTS_INDEX_KEY, JSON.stringify(items));
      localStorage.setItem(PRODUCTS_INDEX_TS_KEY, String(Date.now()));
    } catch (e) {
      /* ignore */
    }
  }

  async function fetchProductsIndexPage(page) {
    const url = new URL(PRODUCTS_INDEX_VIEW_URL, window.location.origin);
    url.searchParams.set("page", String(page));
    return await fetchJSONTimeout(url.toString(), 20000);
  }

  async function buildProductsIndex() {
    if (Array.isArray(productsIndex) && productsIndex.length) return productsIndex;

    const cached = readProductsIndexFromStorage();
    if (cached && cached.length) {
      productsIndex = cached;
      return productsIndex;
    }

    let first;
    try {
      first = await fetchProductsIndexPage(1);
    } catch (e) {
      productsIndex = [];
      return productsIndex;
    }

    const firstProducts = Array.isArray(first && first.products) ? first.products : [];
    const pages = Math.max(1, Math.min(PRODUCTS_INDEX_PAGE_CAP, parseInt(first && first.pages, 10) || 1));

    const byUrl = new Map();
    for (let i = 0; i < firstProducts.length; i++) {
      const it = normalizeProductItem(firstProducts[i]);
      if (it && it.url) byUrl.set(it.url, it);
    }

    productsIndex = Array.from(byUrl.values());
    writeProductsIndexToStorage(productsIndex);

    if (pages > 1) {
      (async () => {
        for (let p = 2; p <= pages; p++) {
          let data;
          try {
            data = await fetchProductsIndexPage(p);
          } catch (e) {
            break;
          }
          const arr = Array.isArray(data && data.products) ? data.products : [];
          if (!arr.length) break;
          for (let i = 0; i < arr.length; i++) {
            const it = normalizeProductItem(arr[i]);
            if (it && it.url && !byUrl.has(it.url)) byUrl.set(it.url, it);
          }
          productsIndex = Array.from(byUrl.values());
          writeProductsIndexToStorage(productsIndex);
        }
      })().catch(() => {});
    }

    return productsIndex;
  }

  function ensureProductsIndex() {
    if (productsIndexPromise) return productsIndexPromise;
    productsIndexPromise = buildProductsIndex();
    return productsIndexPromise;
  }

function countSuggestResults(data) {
    try {
      const res = data && data.resources && data.resources.results;
      if (!res) return 0;
      let n = 0;
      ["queries", "collections", "products", "pages", "articles"].forEach((k) => {
        if (Array.isArray(res[k])) n += res[k].length;
      });
      return n;
    } catch (e) {
      return 0;
    }
  }

  function moneyFromCents(cents) {
    if (typeof cents !== "number") return "";
    try {
      const value = (cents / 100).toFixed(2).replace(".", ",");
      return `${value} €`;
    } catch (e) {
      return "";
    }
  }

  function formatMoneyAny(raw) {
    // Goal: FR display with comma + currency, without guessing too much.
    // Priority 1: Shopify.formatMoney (respects shop settings)
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      // raw can be cents (number) or a string like "16.90"
      if (typeof raw === "number" && isFinite(raw)) {
        return window.Shopify.formatMoney(raw);
      }
      if (typeof raw === "string") {
        const cleaned = raw.replace(/[^0-9.,]/g, "").replace(",", ".");
        const num = parseFloat(cleaned);
        if (isFinite(num)) {
          return window.Shopify.formatMoney(Math.round(num * 100));
        }
      }
    }

    // Priority 2: Intl.NumberFormat
    try {
      if (typeof raw === "number" && isFinite(raw)) {
        // Heuristic: Shopify often returns cents (integer)
        const euros = Number.isInteger(raw) ? raw / 100 : raw;
        return new Intl.NumberFormat("fr-FR", {
          style: "currency",
          currency: "EUR"
        }).format(euros);
      }
      if (typeof raw === "string") {
        const cleaned = raw.replace(/[^0-9.,]/g, "").replace(",", ".");
        const num = parseFloat(cleaned);
        if (isFinite(num)) {
          return new Intl.NumberFormat("fr-FR", {
            style: "currency",
            currency: "EUR"
          }).format(num);
        }
      }
    } catch (e) {}

    // Fallback
    if (typeof raw === "number") return moneyFromCents(raw);
    return (raw || "").toString().replace(".", ",");
  }

  function findDropdown(input) {
    const form = input.closest("form");
    if (!form) return null;
    return form.querySelector(SELECTOR_DROPDOWN);
  }

  function hardKillLiveSearch() {
    const els = document.querySelectorAll(
      ".livesearch, [class*='livesearch'], [id*='livesearch'], [data-livesearch]"
    );
    els.forEach((el) => {
      el.style.display = "none";
      el.style.visibility = "hidden";
      el.style.opacity = "0";
      el.style.pointerEvents = "none";
    });

    const all = document.querySelectorAll("body *");
    all.forEach((node) => {
      if (
        node &&
        node.nodeType === 1 &&
        node.childNodes &&
        node.childNodes.length === 1 &&
        node.textContent &&
        node.textContent.trim().toUpperCase() === "LIVESEARCH"
      ) {
        node.style.display = "none";
      }
    });
  }

  async function fetchSuggest(q) {
    q = cleanSpaces(q);
    if (!q || q.length < MIN_CHARS) return null;

    const cacheKey = baseKey(q);
    if (cacheKey && suggestCache.has(cacheKey)) {
      return suggestCache.get(cacheKey);
    }

    const url = new URL("/search/suggest.json", window.location.origin);
    url.searchParams.set("q", q);
    url.searchParams.set("resources[type]", "product,collection,article,page");
    url.searchParams.set("resources[limit]", String(LIMIT_PER_TYPE));
    url.searchParams.set("resources[options][unavailable_products]", "last");
    const fields = q.length <= 3 ? "title" : "title,product_type,variants.title,variants.sku,vendor,tag,author";

    url.searchParams.set(
      "resources[options][fields]",
      fields
    );

    if (controller) controller.abort();
    controller = new AbortController();

    const res = await fetch(url.toString(), {
      method: "GET",
      headers: { Accept: "application/json" },
      signal: controller.signal,
      credentials: "same-origin",
    });

    if (!res.ok) throw new Error("suggest_failed");

    const json = await res.json();
    if (cacheKey) suggestCache.set(cacheKey, json);
    return json;
  }

  async function fetchFallbackProducts() {
    const res = await fetch(`/products.json?limit=${LIMIT_PER_TYPE}`, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (!res.ok) return [];
    const json = await res.json();
    const products = (json && json.products) || [];
    return products.slice(0, LIMIT_PER_TYPE).map((p) => ({
      id: p.id,
      title: p.title,
      url: p.handle ? `/products/${p.handle}` : "#",
      image: p.images && p.images[0] ? p.images[0].src : "",
      price_cents:
        p.variants && p.variants[0] && typeof p.variants[0].price === "string"
          ? Math.round(parseFloat(p.variants[0].price) * 100)
          : null,
    }));
  }


  function normalizeImgUrl(u) {
    if (!u) return "";
    const s = String(u).trim();
    if (!s) return "";
    if (s.startsWith("//")) return window.location.protocol + s;
    return s;
  }

  function extractImageUrl(item) {
    if (!item) return "";

    const candidates = [
      item.thumb,
      item.image,
      item.featured_image,
      item.featured_image && item.featured_image.url,
      item.featured_image && item.featured_image.src,
      item.image && item.image.url,
      item.image && item.image.src,
      item.image && item.image.originalSrc,
      item.image && item.image.original_src,
    ];

    for (const c of candidates) {
      if (typeof c === "string" && c.trim()) return normalizeImgUrl(c);
      if (c && typeof c === "object") {
        const u = c.url || c.src || c.originalSrc || c.original_src;
        if (typeof u === "string" && u.trim()) return normalizeImgUrl(u);
      }
    }

    if (Array.isArray(item.images) && item.images.length) {
      const first = item.images[0];
      if (typeof first === "string" && first.trim()) return normalizeImgUrl(first);
      if (first && typeof first === "object") {
        const u = first.url || first.src || first.originalSrc || first.original_src;
        if (typeof u === "string" && u.trim()) return normalizeImgUrl(u);
      }
    }

    return "";
  }

  function mapSuggestItem(type, item) {
    if (!item) return null;

    if (type === "products") {
      return {
        title: decodeEntities(item.title || ""),
        url: item.url || "",
        thumb: extractImageUrl(item),
        price: formatMoneyAny(item.price),
      };
    }

    if (type === "collections") {
      return {
        title: decodeEntities(item.title || ""),
        url: item.url || "",
        thumb: extractImageUrl(item),
        price: "",
      };
    }

    if (type === "articles") {
      return {
        title: decodeEntities(item.title || ""),
        url: item.url || "",
        thumb: extractImageUrl(item),
        price: "",
      };
    }

    if (type === "pages") {
      return {
        title: decodeEntities(item.title || ""),
        url: item.url || "",
        thumb: "",
        price: "",
      };
    }

    return null;
  }

  function renderSection(title, items, type) {
    if (!items || !items.length) {
      return `
        <div class="lfdl-ps__section">
          <div class="lfdl-ps__title">${title}</div>
          <div class="lfdl-ps__hint">Pas de résultat dans cette catégorie</div>
        </div>
      `;
    }

    const lis = items
      .map((it) => {
        const placeholder =
          type === "collections" ? "C" : type === "articles" ? "A" : type === "pages" ? "P" : "";

        const thumb =
          it.thumb && it.thumb.length
            ? `<div class="lfdl-ps__thumb"><img src="${it.thumb}" alt="" loading="lazy"></div>`
            : `<div class="lfdl-ps__thumb lfdl-ps__thumb--placeholder" aria-hidden="true">${placeholder}</div>`;

        const sub = it.price ? `<div class="lfdl-ps__sub">${it.price}</div>` : "";
        return `
          <li class="lfdl-ps__item">
            <a class="lfdl-ps__link" href="${it.url}">
              ${thumb}
              <div class="lfdl-ps__meta">
                <div class="lfdl-ps__name">${it.title}</div>
                ${sub}
              </div>
            </a>
          </li>
        `;
      })
      .join("");

    return `
      <div class="lfdl-ps__section">
        <div class="lfdl-ps__title">${title}</div>
        <ul class="lfdl-ps__list">${lis}</ul>
      </div>
    `;
  }

  function buildDropdownHTML(data, qForLink) {
    const resources = (data && data.resources && data.resources.results) || {};
    const products = (resources.products || []).map((x) => mapSuggestItem("products", x)).filter(Boolean);
    const collections = (resources.collections || []).map((x) => mapSuggestItem("collections", x)).filter(Boolean);
    const articles = (resources.articles || []).map((x) => mapSuggestItem("articles", x)).filter(Boolean);
    const pages = (resources.pages || []).map((x) => mapSuggestItem("pages", x)).filter(Boolean);

    return `
      <div class="lfdl-ps__body">
        ${renderSection("PRODUITS", products, "products")}
        ${renderSection("COLLECTIONS", collections, "collections")}
        ${renderSection("ARTICLES", articles, "articles")}
        ${renderSection("PAGES", pages, "pages")}
      </div>
      <div class="lfdl-ps__footer">
        <a class="lfdl-ps__cta" href="/search?q=${encodeURIComponent(qForLink)}">VOIR TOUS LES RÉSULTATS</a>
      </div>
    `;
  }

  function buildFallbackHTML(q) {
  const safeQ = String(q || "").trim();
  const encoded = encodeURIComponent(safeQ);
  return `
    <div class="lfdl-ps__section">
      <div class="lfdl-ps__title">Aucun résultat</div>
      <div class="lfdl-ps__hint">Essayez avec un autre mot ou une autre orthographe</div>
    </div>
    <div class="lfdl-ps__footer">
      <a class="lfdl-ps__cta" href="/search?q=${encoded}">VOIR TOUS LES RÉSULTATS</a>
    </div>
  `;
}

function show(dropdown, html) {
    dropdown.innerHTML = html;
    dropdown.hidden = false;
  }

  function hide(dropdown) {
    dropdown.hidden = true;
    dropdown.innerHTML = "";
  }

  async function smartSearch(queryRaw) {
  const q0 = cleanSpaces(String(queryRaw || ""));
  const qKey = normalize(q0);

  if (qKey.length < MIN_CHARS) {
    return { type: "abort" };
  }

  // Candidate queries help recover from common FR typos (en/an, accents, apostrophes, etc.)
  const candidates = buildCandidateQueries(q0);

  // Load local indexes once per keystroke.
  let localCollections = [];
  let localProducts = [];

  try {
    const idx = await ensureCollectionsIndex();
    localCollections = filterAndRankByTitle(idx || [], q0);
  } catch (e) {
    localCollections = [];
  }

  // Products index can be large: we cap the wait so the dropdown stays snappy.
  try {
    const idxP = await Promise.race([
      ensureProductsIndex(),
      new Promise((resolve) => setTimeout(() => resolve(null), 250)),
    ]);
    if (Array.isArray(idxP) && idxP.length) {
      localProducts = filterAndRankByTitle(idxP, q0);
    }
  } catch (e) {
    localProducts = [];
  }

  let bestData = null;
  let bestQueryForLink = q0;

  for (let i = 0; i < candidates.length; i++) {
    const cq = candidates[i];

    let data = null;
    try {
      data = await fetchSuggest(cq);
    } catch (e) {
      data = null;
    }

    if (!data || !data.resources || !data.resources.results) {
      continue;
    }

    const r = data.resources.results;

    // Title-first filtering + ranking (collections from our local index if available)
    const shopProducts = filterAndRankByTitle(r.products || [], q0);
    const mergedProducts = localProducts.length
      ? filterAndRankByTitle(mergeUnique(shopProducts, localProducts, (it) => it.url), q0)
      : shopProducts;

    const filtered = {
      products: mergedProducts,
      collections: localCollections.length ? localCollections : filterAndRankByTitle(r.collections || [], q0),
      pages: filterAndRankByTitle(r.pages || [], q0),
      articles: filterAndRankByTitle(r.articles || [], q0),
    };

    const out = Object.assign({}, data);
    out.resources = Object.assign({}, data.resources, { results: filtered });

    if (!bestData) {
      bestData = out;
      bestQueryForLink = cq;
    }

    const cnt = countSuggestResults(out);
    if (cnt > 0) {
      bestData = out;
      bestQueryForLink = cq;
      break;
    }
  }

  // If the suggest endpoint failed (or returned nothing) but we have local collections, still show them.
  if (!bestData && localCollections.length) {
    bestData = {
      resources: {
        results: {
          products: [],
          collections: localCollections,
          pages: [],
          articles: [],
        },
      },
    };
  }

  if (bestData && countSuggestResults(bestData) > 0) {
    return { type: "ok", data: bestData, qForLink: bestQueryForLink };
  }

  return { type: "none", qForLink: bestQueryForLink };
}

function ensureDropdown(input) {
  // 1) Explicit link by id
  const explicitId = input.getAttribute("data-lfdl-search-dropdown-id") || input.dataset.lfdlSearchDropdownId;
  if (explicitId) {
    const byId = document.getElementById(explicitId);
    if (byId) {
      byId.classList.add("lfdl-predictive-search");
      byId.setAttribute("data-lfdl-search-dropdown", "");
      return byId;
    }
  }

  // 2) Nearest wrapper / form
  const host =
    input.closest("[data-lfdl-search-wrapper], .lfdl-search-wrapper, predictive-search, .predictive-search, .header__search, .search, form") ||
    input.form ||
    input.parentElement;

  if (host) {
    const existing = host.querySelector(SELECTOR_DROPDOWN) || host.querySelector(".lfdl-predictive-search");
    if (existing) {
      existing.classList.add("lfdl-predictive-search");
      existing.setAttribute("data-lfdl-search-dropdown", "");
      return existing;
    }
  }

  // 3) Create
  const container = host || input.parentElement;
  if (!container) return null;

  try {
    const cs = window.getComputedStyle(container);
    if (cs && cs.position === "static") container.style.position = "relative";
  } catch (e) {}

  const dropdown = document.createElement("div");
  dropdown.className = "lfdl-predictive-search";
  dropdown.setAttribute("data-lfdl-search-dropdown", "");
  dropdown.setAttribute("role", "listbox");
  dropdown.setAttribute("aria-label", "Suggestions de recherche");
  dropdown.hidden = true;

  try {
    input.insertAdjacentElement("afterend", dropdown);
  } catch (e) {
    container.appendChild(dropdown);
  }

  return dropdown;
}

function attachInput(input) {
  try {
    if (!input) return;
    if (input.dataset && input.dataset.lfdlPsBound === "1") return;
    if (input.type === "hidden") return;
    if (input.dataset) input.dataset.lfdlPsBound = "1";

    const dropdown = ensureDropdown(input);
    if (!dropdown) return;

    // Auto-correction à la soumission : si une requête "meilleure" a été trouvée,
    // on remplace la valeur avant d'envoyer /search.
    const form = input.closest("form");
    if (form && form.dataset && form.dataset.lfdlPsSubmitBound !== "1") {
      form.dataset.lfdlPsSubmitBound = "1";
      form.addEventListener(
        "submit",
        () => {
          const best = (input.dataset && input.dataset.lfdlBestQuery) ? input.dataset.lfdlBestQuery : "";
          if (best && best.trim().length >= MIN_CHARS) {
            input.value = best.trim();
          }
        },
        true
      );
    }

    const onInput = () => {
      const q = input.value || "";
      if (normalize(q).length < MIN_CHARS) {
        hide(dropdown);
        return;
      }

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(async () => {
        hardKillLiveSearch();
        const res = await smartSearch(q);

        if (res.type === "ok") {
          if (input.dataset) input.dataset.lfdlBestQuery = (res.qForLink || q).trim();
          show(dropdown, buildDropdownHTML(res.data, res.qForLink || q));
          return;
        }

        if (res.type === "none") {
          if (input.dataset) delete input.dataset.lfdlBestQuery;
          show(dropdown, buildFallbackHTML(res.qForLink || q));
          return;
        }

        if (input.dataset) delete input.dataset.lfdlBestQuery;
        hide(dropdown);
      }, DEBOUNCE_MS);
    };

    let isHoveringDropdown = false;

    dropdown.addEventListener("mouseenter", () => {
      isHoveringDropdown = true;
    });
    dropdown.addEventListener("mouseleave", () => {
      isHoveringDropdown = false;
    });

    const onBlur = () => {
      // Ne pas fermer si l'utilisateur passe du champ à la liste (hover) ou si le focus reste dedans.
      setTimeout(() => {
        const ae = document.activeElement;
        if (isHoveringDropdown) return;
        if (ae && (ae === input || dropdown.contains(ae))) return;
        hide(dropdown);
      }, 200);
    };

    const onFocus = () => {
      if ((input.value || "").trim().length >= MIN_CHARS) onInput();
    };

    input.addEventListener("input", onInput);
    input.addEventListener("focus", onFocus);
    input.addEventListener("blur", onBlur);

    document.addEventListener("click", (e) => {
      if (!dropdown.contains(e.target) && e.target !== input) hide(dropdown);
    });
  
  } catch (e) {
    console.warn("LFDL predictive search attachInput error", e);
  }
}

  function init() {
    hardKillLiveSearch();
    const inputs = Array.from(document.querySelectorAll(SELECTOR_INPUT));
    inputs.forEach(attachInput);

    if (window.Shopify && Shopify.designMode) {
      document.addEventListener("shopify:section:load", () => {
        hardKillLiveSearch();
        const inputs2 = Array.from(document.querySelectorAll(SELECTOR_INPUT));
        inputs2.forEach(attachInput);
      });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
